import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LanguageRow, LanguageService } from '@app/services/language.service';
import { ToastrService } from 'ngx-toastr';

type LangSortField = 'LanguageShortCode' | 'LanguageName';

@Component({
  selector: 'app-manage-languages',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-manage-languages.component.html',
})
export class ManageLanguagesComponent implements OnInit {
  languageList: LanguageRow[] = [];
  filteredLanguages: LanguageRow[] = [];
  loading = true;
  error = '';
  searchText = '';
  sortField: LangSortField = 'LanguageShortCode';
  sortAsc = true;
  editingCode: string | null = null;
  editRow: LanguageRow = {
    LanguageShortCode: '',
    LanguageName: '',
    ScriptName: '',
    TranslationInstruction: '',
    KeepTechnicalInEnglish: 'Y',
    TechnicalEnglishScript: 'ROMAN',
  };

  newLanguage: LanguageRow = {
    LanguageShortCode: '',
    LanguageName: '',
    ScriptName: '',
    TranslationInstruction: '',
    KeepTechnicalInEnglish: 'Y',
    TechnicalEnglishScript: 'ROMAN',
  };

  constructor(
    private languageService: LanguageService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadLanguages();
  }

  loadLanguages(): void {
    this.loading = true;
    this.languageService.getLanguages().subscribe({
      next: (res: LanguageRow[]) => {
        this.languageList = Array.isArray(res) ? res : [];
        this.applyFilterAndSort();
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load languages.';
        this.toastr.error(this.error);
        this.loading = false;
      },
    });
  }

  addLanguage(): void {
    const code = String(this.newLanguage.LanguageShortCode || '').trim();
    const name = String(this.newLanguage.LanguageName || '').trim();
    if (!code || !name) {
      this.toastr.warning('Short code and name are required');
      return;
    }
    this.languageService.addLanguage(this.newLanguage).subscribe({
      next: () => {
        this.toastr.success('Language added');
        this.newLanguage = {
          LanguageShortCode: '',
          LanguageName: '',
          ScriptName: '',
          TranslationInstruction: '',
          KeepTechnicalInEnglish: 'Y',
          TechnicalEnglishScript: 'ROMAN',
        };
        this.loadLanguages();
      },
      error: (err) => {
        const msg = err?.error?.Data?.message || err?.error?.message || 'Failed to add language';
        this.toastr.error(msg);
      },
    });
  }

  startEdit(row: LanguageRow): void {
    this.editingCode = row.LanguageShortCode;
    this.editRow = {
      ...row,
      KeepTechnicalInEnglish: row.KeepTechnicalInEnglish === 'N' ? 'N' : 'Y',
      TechnicalEnglishScript: row.TechnicalEnglishScript === 'NATIVE' ? 'NATIVE' : 'ROMAN',
    };
  }

  cancelEdit(): void {
    this.editingCode = null;
  }

  saveEdit(): void {
    if (!this.editingCode) return;
    this.languageService.updateLanguage(this.editRow).subscribe({
      next: () => {
        this.toastr.success('Language updated');
        this.editingCode = null;
        this.loadLanguages();
      },
      error: (err) => {
        const msg = err?.error?.Data?.message || err?.error?.message || 'Failed to update language';
        this.toastr.error(msg);
      },
    });
  }

  onSearchChange(): void {
    this.applyFilterAndSort();
  }

  setSort(field: LangSortField): void {
    if (this.sortField === field) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortField = field;
      this.sortAsc = true;
    }
    this.applyFilterAndSort();
  }

  sortIndicator(field: LangSortField): string {
    if (this.sortField !== field) return '';
    return this.sortAsc ? ' ▲' : ' ▼';
  }

  private applyFilterAndSort(): void {
    const q = this.searchText.trim().toLowerCase();
    let rows = [...this.languageList];
    if (q) {
      rows = rows.filter(
        (l) =>
          String(l.LanguageShortCode || '').toLowerCase().includes(q) ||
          String(l.LanguageName || '').toLowerCase().includes(q) ||
          String(l.ScriptName || '').toLowerCase().includes(q) ||
          String(l.TranslationInstruction || '').toLowerCase().includes(q),
      );
    }
    const field = this.sortField;
    const dir = this.sortAsc ? 1 : -1;
    rows.sort((a, b) => {
      const av = String(a[field] || '').toLowerCase();
      const bv = String(b[field] || '').toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    this.filteredLanguages = rows;
  }
}
